import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Waves, ArrowLeft, Sun, Moon, List, X, ShieldCheck, Envelope } from '@phosphor-icons/react';
import { Logo, LogoText } from '../components/Logo';

export default function Refund() {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="refund-logo">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <Link to="/about" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refund-nav-about">About</Link>
            <Link to="/faq" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refund-nav-faq">FAQ</Link>
            <Link to="/pricing" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refund-nav-pricing">Pricing</Link>
            <Link to="/docs" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refund-nav-docs">API Docs</Link>
            <Link to="/support" className="text-sm" style={{ color: 'var(--text-secondary)' }} data-testid="refund-nav-support">Support</Link>
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }} data-testid="refund-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/register" className="btn-primary text-sm" data-testid="refund-nav-get-started">Get Started</Link>
          </div>
          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-secondary)' }} data-testid="refund-mobile-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" data-testid="refund-mobile-menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t px-4 py-4 space-y-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Pricing</Link>
            <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            <Link to="/support" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Support</Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Login</Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn-primary text-sm text-center block">Get Started</Link>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-24 sm:pt-32 pb-10 sm:pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm transition-colors mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="refund-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-bg)', border: '1px solid var(--border-accent)' }}>
            <ShieldCheck weight="fill" className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <span className="text-xs tracking-wider uppercase font-semibold" style={{ color: 'var(--accent)' }}>Refund Policy</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4" data-testid="refund-heading">
            Refund <span style={{ color: 'var(--accent)' }}>Policy</span>
          </h1>
          <p className="text-sm sm:text-base max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }} data-testid="refund-subtitle">
            We stand behind index24hrs.com. If something isn't working for you, here's exactly how refunds work.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Eligibility */}
          <div className="border rounded-xl p-5 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid="refund-eligibility">
            <h2 className="text-lg font-bold mb-3">Eligibility</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              You can request a refund within <strong>7 days</strong> of your purchase date. This applies to all credit packages, regardless of how many credits you've already used.
            </p>
          </div>

          {/* Non-Refundable */}
          <div className="border rounded-xl p-5 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid="refund-non-refundable">
            <h2 className="text-lg font-bold mb-3">Non-Refundable Situations</h2>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>Refunds won't be issued in the following cases:</p>
            <ul className="space-y-2">
              {[
                'The request is submitted more than 7 days after the purchase date',
                'The account has been suspended or terminated for violating our Terms of Service',
                "The issue is caused by a search engine's decision not to index a submitted URL, that falls outside our control",
                'The same order has already been refunded previously'
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-red-400" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* How to Request */}
          <div className="border rounded-xl p-5 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid="refund-how-to">
            <h2 className="text-lg font-bold mb-3">How to Request a Refund</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Visit our <Link to="/support" className="font-medium hover:underline" style={{ color: 'var(--accent)' }}>Support Center</Link> with your account email, purchase date, and transaction ID. You don't need to give a reason, though we're always happy to hear feedback. We'll confirm receipt within one business day.
            </p>
          </div>

          {/* Processing Time */}
          <div className="border rounded-xl p-5 sm:p-6" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }} data-testid="refund-processing">
            <h2 className="text-lg font-bold mb-3">Processing Time</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
              Once we verify eligibility, refunds are approved within <strong>3 business days</strong> and returned to your original payment method. Depending on your bank or card provider, it may take an additional 5–10 business days for the amount to appear in your account.
            </p>
          </div>

          {/* Questions */}
          <div className="border rounded-xl p-6 sm:p-8 text-center" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)', boxShadow: 'var(--shadow-card)' }} data-testid="refund-questions">
            <h2 className="text-lg font-bold mb-2">Questions?</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>Visit our Support Center. We typically respond within 24 hours.</p>
            <Link to="/support" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="refund-contact-btn">
              <Envelope weight="fill" className="w-5 h-5" /> Contact Support
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-bold text-sm">index24hrs<span style={{ color: 'var(--accent)' }}>.com</span></span>
          </div>
          <p className="text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>© 2026 index24hrs.com. Instant indexing for the modern web.</p>
        </div>
      </footer>
    </div>
  );
}
